import { useState } from 'react';
import {
  App as AntApp,
  Button,
  Card,
  DatePicker,
  Form,
  Input,
  Modal,
  Popconfirm,
  Segmented,
  Space,
  Table,
  Tag,
  Typography,
} from 'antd';
import {
  CheckOutlined,
  CloseOutlined,
  DollarOutlined,
  PauseCircleOutlined,
  PlayCircleOutlined,
  StopOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs, { type Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { ApiError } from '@starling/core/api/errors';
import { ErrorAlert } from '@/components/ErrorAlert';
import { formatDateTime, formatNumber } from '@/lib/format';
import {
  ADMIN_ADS_PAGE_SIZE,
  ADMIN_ADS_STATES,
  useActivateAdMutation,
  useAdminAdsQuery,
  useApproveAdMutation,
  usePauseAdMutation,
  useRejectAdMutation,
  useResumeAdMutation,
  useStopAdMutation,
  type AdStatus,
  type AdminAd,
  type AdminAdsState,
} from './adsHooks';

const { Text } = Typography;

// Вкладка «Реклама» раздела «Модерация» (V1.4, бриф 20260911 §4): заявки на
// баннеры Starling Vision по сегментам `?state=` — На модерации / Ждут оплаты /
// Активные / Архив. Действия по состоянию:
//   * pending  — «Одобрить» (Popconfirm → письмо с суммой и реквизитами) и
//                «Отклонить» (Modal с обязательной причиной);
//   * approved — «Оплачено» (Modal с датой начала показа, по умолчанию
//                сейчас; пустая дата → тело без starts_at) и «Отклонить»;
//   * active   — «Приостановить» (Popconfirm → paused, обратимо) и «Снять»
//                (Popconfirm → stopped, окончательно);
//   * paused   — «Возобновить» (→ active, `ends_at` сдвигается на длительность
//                паузы) и «Снять». Paused живёт в сегменте «Активные» вместе
//                с active (`?state=active` на бэке включает оба) — в колонке
//                «Показ» вместо «до …» показываем «на паузе с …».
// 409 `ad_invalid_transition` — статус сменился с другого экрана: говорим об
// этом и перечитываем (инвалидация — в самих мутациях, onSettled).

export function parseAdsState(raw: string | null): AdminAdsState {
  return (ADMIN_ADS_STATES as readonly string[]).includes(raw ?? '')
    ? (raw as AdminAdsState)
    : 'pending';
}

function AdStatusTag({ status }: { status: AdStatus }): JSX.Element {
  const { t } = useTranslation();
  const label = t(`platform.moderation.ads.status.${status}`);
  switch (status) {
    case 'pending':
      return <Tag color="gold">{label}</Tag>;
    case 'approved':
      return <Tag color="blue">{label}</Tag>;
    case 'active':
      return <Tag color="green">{label}</Tag>;
    case 'paused':
      return <Tag color="warning">{label}</Tag>;
    case 'rejected':
      return <Tag color="red">{label}</Tag>;
    case 'expired':
    case 'stopped':
    case 'withdrawn':
      return <Tag>{label}</Tag>;
  }
}

interface RejectFormValues {
  reason: string;
}

interface ActivateFormValues {
  starts_at: Dayjs | null;
}

export function AdsModerationTab(): JSX.Element {
  const { t } = useTranslation();
  const { message } = AntApp.useApp();
  const [searchParams, setSearchParams] = useSearchParams();

  const state = parseAdsState(searchParams.get('state'));
  const page = Number(searchParams.get('page') ?? '1') || 1;

  const { data, isLoading, error } = useAdminAdsQuery({ state, page });
  const approveMutation = useApproveAdMutation();
  const rejectMutation = useRejectAdMutation();
  const activateMutation = useActivateAdMutation();
  const stopMutation = useStopAdMutation();
  const pauseMutation = usePauseAdMutation();
  const resumeMutation = useResumeAdMutation();

  const [rejecting, setRejecting] = useState<AdminAd | null>(null);
  const [activating, setActivating] = useState<AdminAd | null>(null);
  const [rejectForm] = Form.useForm<RejectFormValues>();
  const [activateForm] = Form.useForm<ActivateFormValues>();

  const onError = (err: unknown): void => {
    // 409 — переход невозможен из текущего статуса: заявку уже одобрили /
    // отозвали / сняли в другом окне. Список перечитывается мутацией.
    if (err instanceof ApiError && err.status === 409) {
      void message.error(t('platform.moderation.ads.conflict'));
      return;
    }
    void message.error(err instanceof Error ? err.message : t('errors.generic'));
  };

  const approve = (ad: AdminAd): void => {
    approveMutation.mutate(
      { adId: ad.id },
      { onSuccess: () => void message.success(t('platform.moderation.ads.approve_ok')), onError },
    );
  };

  const stop = (ad: AdminAd): void => {
    stopMutation.mutate(
      { adId: ad.id },
      { onSuccess: () => void message.success(t('platform.moderation.ads.stop_ok')), onError },
    );
  };

  const pause = (ad: AdminAd): void => {
    pauseMutation.mutate(
      { adId: ad.id },
      { onSuccess: () => void message.success(t('platform.moderation.ads.pause_ok')), onError },
    );
  };

  const resume = (ad: AdminAd): void => {
    resumeMutation.mutate(
      { adId: ad.id },
      { onSuccess: () => void message.success(t('platform.moderation.ads.resume_ok')), onError },
    );
  };

  const submitReject = (values: RejectFormValues): void => {
    if (!rejecting) return;
    rejectMutation.mutate(
      { adId: rejecting.id, reason: values.reason.trim() },
      {
        onSuccess: () => {
          setRejecting(null);
          rejectForm.resetFields();
          void message.success(t('platform.moderation.ads.reject_ok'));
        },
        onError: (err) => {
          setRejecting(null);
          onError(err);
        },
      },
    );
  };

  const submitActivate = (values: ActivateFormValues): void => {
    if (!activating) return;
    const startsAt = values.starts_at ? values.starts_at.toISOString() : undefined;
    activateMutation.mutate(
      { adId: activating.id, ...(startsAt ? { startsAt } : {}) },
      {
        onSuccess: () => {
          setActivating(null);
          void message.success(t('platform.moderation.ads.activate_ok'));
        },
        onError: (err) => {
          setActivating(null);
          onError(err);
        },
      },
    );
  };

  const openActivate = (ad: AdminAd): void => {
    activateForm.setFieldsValue({ starts_at: dayjs() });
    setActivating(ad);
  };

  const rejectButton = (ad: AdminAd): JSX.Element => (
    <Button
      size="small"
      danger
      icon={<CloseOutlined />}
      loading={rejectMutation.isPending && rejectMutation.variables?.adId === ad.id}
      onClick={() => {
        rejectForm.resetFields();
        setRejecting(ad);
      }}
    >
      {t('platform.moderation.ads.reject')}
    </Button>
  );

  // «Снять» — окончательно (active | paused → stopped); Popconfirm об этом предупреждает.
  const stopButton = (ad: AdminAd): JSX.Element => (
    <Popconfirm
      title={t('platform.moderation.ads.stop_confirm')}
      okText={t('platform.moderation.ads.stop')}
      cancelText={t('common.cancel')}
      okButtonProps={{ danger: true }}
      onConfirm={() => stop(ad)}
    >
      <Button
        size="small"
        danger
        icon={<StopOutlined />}
        loading={stopMutation.isPending && stopMutation.variables?.adId === ad.id}
      >
        {t('platform.moderation.ads.stop')}
      </Button>
    </Popconfirm>
  );

  const actions = (ad: AdminAd): JSX.Element | null => {
    switch (ad.status) {
      case 'pending':
        return (
          <Space direction="vertical" size={6} style={{ display: 'flex', alignItems: 'flex-end' }}>
            <Popconfirm
              title={t('platform.moderation.ads.approve_confirm')}
              okText={t('platform.moderation.ads.approve')}
              cancelText={t('common.cancel')}
              onConfirm={() => approve(ad)}
            >
              <Button
                size="small"
                type="primary"
                icon={<CheckOutlined />}
                loading={approveMutation.isPending && approveMutation.variables?.adId === ad.id}
              >
                {t('platform.moderation.ads.approve')}
              </Button>
            </Popconfirm>
            {rejectButton(ad)}
          </Space>
        );
      case 'approved':
        return (
          <Space direction="vertical" size={6} style={{ display: 'flex', alignItems: 'flex-end' }}>
            <Button
              size="small"
              type="primary"
              icon={<DollarOutlined />}
              loading={activateMutation.isPending && activateMutation.variables?.adId === ad.id}
              onClick={() => openActivate(ad)}
            >
              {t('platform.moderation.ads.activate')}
            </Button>
            {rejectButton(ad)}
          </Space>
        );
      case 'active':
        return (
          <Space direction="vertical" size={6} style={{ display: 'flex', alignItems: 'flex-end' }}>
            <Popconfirm
              title={t('platform.moderation.ads.pause_confirm')}
              okText={t('platform.moderation.ads.pause')}
              cancelText={t('common.cancel')}
              onConfirm={() => pause(ad)}
            >
              <Button
                size="small"
                icon={<PauseCircleOutlined />}
                loading={pauseMutation.isPending && pauseMutation.variables?.adId === ad.id}
              >
                {t('platform.moderation.ads.pause')}
              </Button>
            </Popconfirm>
            {stopButton(ad)}
          </Space>
        );
      case 'paused':
        return (
          <Space direction="vertical" size={6} style={{ display: 'flex', alignItems: 'flex-end' }}>
            <Popconfirm
              title={t('platform.moderation.ads.resume_confirm')}
              okText={t('platform.moderation.ads.resume')}
              cancelText={t('common.cancel')}
              onConfirm={() => resume(ad)}
            >
              <Button
                size="small"
                type="primary"
                icon={<PlayCircleOutlined />}
                loading={resumeMutation.isPending && resumeMutation.variables?.adId === ad.id}
              >
                {t('platform.moderation.ads.resume')}
              </Button>
            </Popconfirm>
            {stopButton(ad)}
          </Space>
        );
      default:
        return null;
    }
  };

  const columns: ColumnsType<AdminAd> = [
    {
      title: t('platform.moderation.ads.table.banner'),
      key: 'banner',
      width: 140,
      render: (_v, ad) => (
        <img
          src={ad.image_url}
          alt=""
          width={120}
          height={50}
          style={{ width: 120, height: 50, objectFit: 'cover', borderRadius: 6, display: 'block' }}
        />
      ),
    },
    {
      title: t('platform.moderation.ads.table.title'),
      key: 'title',
      render: (_v, ad) => (
        <Space direction="vertical" size={2}>
          <Text strong style={{ fontSize: 13 }}>
            {ad.title}
          </Text>
          <a
            href={ad.link_url}
            target="_blank"
            rel="noreferrer noopener"
            style={{ fontSize: 12, wordBreak: 'break-all' }}
          >
            {ad.link_url}
          </a>
          {ad.comment && (
            <Text type="secondary" italic style={{ fontSize: 12 }}>
              {ad.comment}
            </Text>
          )}
        </Space>
      ),
    },
    {
      title: t('platform.moderation.ads.table.organization'),
      key: 'organization',
      width: 180,
      render: (_v, ad) => (
        <Space direction="vertical" size={2}>
          <Text>{ad.organization_name}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {ad.applicant_email ?? '—'}
          </Text>
        </Space>
      ),
    },
    {
      title: t('platform.moderation.ads.table.terms'),
      key: 'terms',
      width: 150,
      render: (_v, ad) => (
        <Space direction="vertical" size={2}>
          <Text>{t('platform.moderation.ads.weeks', { count: ad.weeks })}</Text>
          <Text strong>{formatNumber(ad.price_kzt)} ₸</Text>
        </Space>
      ),
    },
    {
      title: t('platform.moderation.ads.table.date'),
      dataIndex: 'created_at',
      key: 'created_at',
      width: 150,
      render: (v: string) => (
        <Text type="secondary" className="sl-mono" style={{ fontSize: 12 }}>
          {formatDateTime(v)}
        </Text>
      ),
    },
  ];

  if (state === 'active') {
    columns.push({
      title: t('platform.moderation.ads.table.run'),
      key: 'run',
      width: 190,
      render: (_v, ad) => (
        // Колонка «Показ» — один вертикальный блок: статус, срок/пауза, счётчики
        // (замечание пользователя 2026-09-11: «на паузе с …» не помещалось в строку).
        <Space direction="vertical" size={2}>
          {ad.status === 'paused' ? (
            <>
              <AdStatusTag status={ad.status} />
              <Text style={{ fontSize: 12 }}>
                {t('platform.moderation.ads.paused_since', {
                  date: formatDateTime(ad.paused_at),
                })}
              </Text>
            </>
          ) : (
            <Text style={{ fontSize: 12 }}>
              {t('platform.moderation.ads.until', { date: formatDateTime(ad.ends_at) })}
            </Text>
          )}
          <Text type="secondary" style={{ fontSize: 12 }}>
            {t('platform.moderation.ads.stats', {
              impressions: formatNumber(ad.impressions),
              clicks: formatNumber(ad.clicks),
            })}
          </Text>
        </Space>
      ),
    });
  }

  if (state === 'archive') {
    columns.push({
      title: t('platform.moderation.ads.table.status'),
      key: 'status',
      width: 220,
      render: (_v, ad) => (
        <Space direction="vertical" size={4}>
          <AdStatusTag status={ad.status} />
          {ad.status === 'rejected' && ad.reject_reason && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              {ad.reject_reason}
            </Text>
          )}
          {(ad.status === 'expired' || ad.status === 'stopped') && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              {t('platform.moderation.ads.stats', {
                impressions: formatNumber(ad.impressions),
                clicks: formatNumber(ad.clicks),
              })}
            </Text>
          )}
        </Space>
      ),
    });
  } else {
    columns.push({
      title: t('platform.moderation.table.action'),
      key: 'action',
      width: 170,
      align: 'right',
      render: (_v, ad) => actions(ad),
    });
  }

  const onStateChange = (next: AdminAdsState): void => {
    const sp = new URLSearchParams(searchParams);
    sp.set('state', next);
    sp.delete('page');
    setSearchParams(sp);
  };

  return (
    <>
      {error && (
        <div style={{ marginTop: 16 }}>
          <ErrorAlert error={error} />
        </div>
      )}

      <Card style={{ marginTop: 16 }} variant="outlined">
        <Segmented<AdminAdsState>
          value={state}
          onChange={onStateChange}
          options={ADMIN_ADS_STATES.map((key) => ({
            value: key,
            label: t(`platform.moderation.ads.states.${key}`),
          }))}
        />
      </Card>

      <Card style={{ marginTop: 16 }} variant="outlined" styles={{ body: { padding: 0 } }}>
        <Table<AdminAd>
          rowKey="id"
          loading={isLoading}
          dataSource={data?.items ?? []}
          columns={columns}
          pagination={{
            current: page,
            pageSize: ADMIN_ADS_PAGE_SIZE,
            total: data?.total ?? 0,
            showSizeChanger: false,
            onChange: (p) => {
              const sp = new URLSearchParams(searchParams);
              sp.set('page', String(p));
              setSearchParams(sp);
            },
          }}
          locale={{ emptyText: t(`platform.moderation.ads.empty.${state}`) }}
        />
      </Card>

      <Modal
        open={rejecting !== null}
        title={t('platform.moderation.ads.reject_title')}
        okText={t('platform.moderation.ads.reject')}
        cancelText={t('common.cancel')}
        okButtonProps={{ danger: true, loading: rejectMutation.isPending }}
        onOk={() => rejectForm.submit()}
        onCancel={() => setRejecting(null)}
        destroyOnClose
      >
        {rejecting && (
          <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
            {rejecting.title} · {rejecting.organization_name}
          </Text>
        )}
        <Form<RejectFormValues> form={rejectForm} layout="vertical" onFinish={submitReject}>
          <Form.Item
            label={t('platform.moderation.ads.reject_reason')}
            name="reason"
            rules={[
              {
                required: true,
                whitespace: true,
                message: t('platform.moderation.ads.reject_reason_required'),
              },
            ]}
          >
            <Input.TextArea rows={3} maxLength={500} showCount data-testid="ad-reject-reason" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        open={activating !== null}
        title={t('platform.moderation.ads.activate_title')}
        okText={t('platform.moderation.ads.activate_submit')}
        cancelText={t('common.cancel')}
        okButtonProps={{ loading: activateMutation.isPending }}
        onOk={() => activateForm.submit()}
        onCancel={() => setActivating(null)}
        destroyOnClose
      >
        {activating && (
          <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
            {t('platform.moderation.ads.activate_text', {
              title: activating.title,
              amount: formatNumber(activating.price_kzt),
              weeks: t('platform.moderation.ads.weeks', { count: activating.weeks }),
            })}
          </Text>
        )}
        <Form<ActivateFormValues> form={activateForm} layout="vertical" onFinish={submitActivate}>
          <Form.Item
            label={t('platform.moderation.ads.starts_at')}
            name="starts_at"
            extra={t('platform.moderation.ads.starts_at_hint')}
          >
            <DatePicker showTime format="DD.MM.YYYY HH:mm" style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}
